import type { ConfirmFieldDefinition, PublicFormField } from "./types.ts";

/** A resolved CONFIRM field paired with its target, ready for value matching. */
export type ConfirmPair = {
  confirm: ConfirmFieldDefinition;
  target: PublicFormField;
};

const CONFIRM_MISMATCH_MESSAGE = "The values do not match.";

// Field types whose submitted value is normalized before a confirm comparison.
type NormalizableType = PublicFormField["type"];

/** Extracts the CONFIRM/target pairs whose targets exist in the definition. */
export const resolveConfirmPairs = (fields: readonly PublicFormField[]): ConfirmPair[] => {
  const byId = new Map<string, PublicFormField>(fields.map((field) => [field.id, field]));
  const pairs: ConfirmPair[] = [];

  for (const field of fields) {
    if (field.type !== "CONFIRM") {
      continue;
    }

    const target = byId.get(field.configJson.targetFieldId);
    if (target) {
      pairs.push({ confirm: field, target });
    }
  }

  return pairs;
};

/** Updates confirmation controls by name, scoped to this form rather than renderer-specific markup. */
export const synchronizeConfirmFields = (
  form: HTMLFormElement,
  fields: readonly PublicFormField[],
): void => {
  const controls = new Map(
    Array.from(
      form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("input, textarea"),
    ).map((control) => [control.name, control]),
  );

  for (const { confirm, target } of resolveConfirmPairs(fields)) {
    const control = controls.get(confirm.key);
    if (!control) {
      continue;
    }

    const targetControl = controls.get(target.key);
    if (!targetControl) {
      control.setCustomValidity("The confirmation target is unavailable.");
      continue;
    }

    const multiline = target.type === "TEXT" && Boolean(target.configJson?.multiline);
    control.setCustomValidity(
      getConfirmValidationMessage(target.type, targetControl.value, control.value, multiline),
    );
  }
};

/**
 * Returns the confirm mismatch message for a pair, or "" when the values match.
 *
 * Both values are normalized with the target-type's scalar semantics before
 * comparison so trivial formatting differences never block a genuine match. The
 * message never contains either answer.
 */
export const getConfirmValidationMessage = (
  targetType: NormalizableType,
  targetValue: string,
  confirmValue: string,
  multiline = false,
): string => {
  const normalizedTarget = normalizeScalar(targetType, targetValue, multiline);
  const normalizedConfirm = normalizeScalar(targetType, confirmValue, multiline);

  if (normalizedTarget === normalizedConfirm) {
    return "";
  }

  return CONFIRM_MISMATCH_MESSAGE;
};

/** Normalizes a raw scalar answer using the semantics of its field type. */
export const normalizeScalar = (
  type: NormalizableType,
  value: string,
  multiline = false,
): string => {
  if (value.trim().length === 0) {
    return "";
  }

  switch (type) {
    case "EMAIL":
    case "PHONE":
    case "WEBSITE":
      return value.trim();
    case "TEXT":
      if (multiline) {
        return value;
      }

      return value.replace(/\r\n|[\r\n]/g, " ");
    case "NUMBER":
      return normalizeNumber(value);
    case "DATETIME":
      return normalizeDateTime(value);
    default:
      return value;
  }
};

const normalizeNumber = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return "";
  }

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    return trimmed;
  }

  return String(parsed);
};

// Native datetime-local values omit seconds when the user picks whole minutes.
// Appending ":00" makes "2024-01-01T09:30" and "2024-01-01T09:30:00" compare equal.
const normalizeDateTime = (value: string): string => {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed)) {
    return `${trimmed}:00`;
  }

  if (/^\d{2}:\d{2}$/.test(trimmed)) {
    return `${trimmed}:00`;
  }

  return trimmed;
};
